import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useForm, FormProvider, useWatch, useFieldArray } from "react-hook-form";
import {
    DocumentEntityTypesEnum,
    DocumentService,
    DocumentTypesEnum,
    EntityType,
    PaymentService,
    PaidFlowTypesEnum,
    PaymentDueAccountVM,
} from "@propertelligent/client-api";
import apiHelper from "../../utils/apiHelper";
import UtilHelper from "../../utils/UtilHelper";
import { useCurrencySymbol } from "../../hooks/useCurrencySymbol";
import CurrencyTooltip from "../../components/currencytooltip/CurrencyTooltip";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import InputField from "../form-fields/InputField";
import SelectField from "../form-fields/SelectField";
import SimpleButton from "../buttons/Simple-button";
import SimpleOutlineButton from "../buttons/Simple-Outline-button"; // ✅ Added Import
import DocumentUploader from "../CustomUploader/DocumentUploader";
import RightSideModal from "../Modal/right-side-modal";
import SimpleSpinner from "../../components/loader";
import DateHelper from "../../utils/DateHelper";
import { UserLoginData } from "../../components/common/charts/Enums";
import { GetImages } from "../../utils/GetImages";
import { utcDateFormat } from "../../utils/helper";
import { Accordion, AccordionSection } from "../card/accordion";
import { DeleteRedIcon } from "../../assets/svgs/DeleteRedIcon";
import "./AddPaymentModal.css";
import CurrencyInput from "../form-fields/CurrencyInput";

// Chevron Icon Component
const ChevronDownIcon = ({ className = "" }: { className?: string }) => (
    <svg width="12" height="7" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
        <path d="M11 1L6 6L1 1" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

interface EditPayableModalProps {
    isOpen: boolean;
    onClose: () => void;
    data: PaymentDueAccountVM | null;
    onSuccess: () => void;
}

interface PaymentItem {
    id?: number;
    PaymentType: string;
    Amount: string;
    DueDate: string;
    GracePeriod: string;
    Description: string;
}

interface FormValues {
    PayeeId: string;
    AttachPaymentId: string;
    PropertyId?: string;
    UnitId?: string;
    documentfile?: File | null;
    AccountNumber?: string;
    InvoiceNo?: string;
    payments: PaymentItem[];
}

interface AccordionState {
    basicDetailsOpen: boolean;
    paymentInfoOpen: boolean;
    basicDetailsComplete: boolean;
}

const EditPayableModal: React.FC<EditPayableModalProps> = ({ isOpen, onClose, data, onSuccess }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isFetchingData, setIsFetchingData] = useState(false);
    const [paymentTypes, setPaymentTypes] = useState<any[]>([]);
    const [resetCounter, setResetCounter] = useState(0);
    const [activePaymentIndex, setActivePaymentIndex] = useState<number | null>(0);
    const [originalDocumentId, setOriginalDocumentId] = useState<number | null>(null);
    const [originalPaymentIds, setOriginalPaymentIds] = useState<number[]>([]);

    const organizationId = Number(sessionStorage.getItem(UserLoginData.organization_id));
    const currencySymbol = useCurrencySymbol();

    const [accordionState, setAccordionState] = useState<AccordionState>({
        basicDetailsOpen: true,
        paymentInfoOpen: false,
        basicDetailsComplete: false,
    });

    const methods = useForm<FormValues>({
        defaultValues: {
            PayeeId: "",
            AttachPaymentId: "",
            AccountNumber: "",
            InvoiceNo: "",
            payments: [],
        },
        mode: "onSubmit",
    });

    const { control, handleSubmit, reset, trigger, formState: { errors } } = methods;

    // ✅ Destructured append and remove
    const { fields, append, remove } = useFieldArray({ control, name: "payments" });
    const watchedPayments = useWatch({ control, name: "payments" });

    // --- 1. Fetch Invoice Items on Open ---
    const fetchInvoiceItems = useCallback(async () => {
        if (!data?.referenceNumber) return;
        setIsFetchingData(true);

        try {
            const res = await apiHelper(
                PaymentService.getApiPaymentDueAccountInvoiceitems,
                { showNotification: false },
                data.referenceNumber
            );

            const items = res?.data?.success || [];

            if (items.length > 0) {
                const firstItem = items[0];

                if (data.payeeAccountTypeId) {
                    fetchPaymentTypes(data.payeeAccountTypeId);
                }

                let fetchedFile = null;
                if (firstItem.id) {
                    fetchedFile = await fetchDocument(firstItem.id);
                }

                const formData: FormValues = {
                    PayeeId: String(data.payeeAccountId),
                    AttachPaymentId: firstItem.formatted?.EntityTypeId || "Organization",
                    AccountNumber: firstItem.accountNumber,
                    InvoiceNo: firstItem.referenceNumber,
                    documentfile: fetchedFile,
                    payments: items.map((item: any) => ({
                        id: item.id,
                        PaymentType: String(item.paymentTypeId),
                        Amount: String(item.amount),
                        DueDate: DateHelper.convertUTCtoDateTime2(item.dueDate),
                        GracePeriod: String(item.gracePeriodInDays),
                        Description: item.description,
                    })),
                };

                if (firstItem.formatted?.EntityTypeId === "Unit") {
                    formData.UnitId = String(firstItem.entityId);
                } else if (firstItem.formatted?.EntityTypeId === "Property") {
                    formData.PropertyId = String(firstItem.entityId);
                }

                reset(formData);

                const loadedIds = formData.payments
    .map(p => p.id)
    .filter((id): id is number => !!id && id > 0);

setOriginalPaymentIds(loadedIds);

                setAccordionState({
                    basicDetailsOpen: true,
                    paymentInfoOpen: false,
                    basicDetailsComplete: false
                });
            }
        } catch (error) {
            PubSub.publish(TopicsEnum.Notification.toString(), {
                message: "Failed to fetch invoice details",
                type: NotificationType.Failure,
            });
        } finally {
            setIsFetchingData(false);
        }
    }, [data, reset]);

    const fetchDocument = async (paymentId: number) => {
        try {
            const res = await apiHelper(
                DocumentService.getApiDocumentBase64,
                { showNotification: false },
                paymentId,
                DocumentEntityTypesEnum.Payment,
                DocumentTypesEnum.PaymentReference
            );
            const items = res?.data?.success;
            if (items && items.length > 0) {
                const doc = items[0];
                setOriginalDocumentId(doc.id);
                const byteCharacters = atob(doc.base64);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: "application/pdf" });
                return new File([blob], doc.fileName || "invoice.pdf", { type: "application/pdf" });
            }
        } catch (e) { return null; }
        setOriginalDocumentId(null);
        return null;
    };

    const fetchPaymentTypes = async (typeId: number) => {
        const res = await apiHelper(PaymentService.getApiPayeeAccountPaymentTypes, { showNotification: false }, typeId);
        setPaymentTypes(res?.data?.success || []);
    };

    useEffect(() => {
        if (isOpen && data) {
            fetchInvoiceItems();
        }
    }, [isOpen, data, fetchInvoiceItems]);

    // --- Toggle Logic ---
    const toggleAccordion = useCallback(async (section: keyof AccordionState) => {
        if (section === "paymentInfoOpen") {
            if (accordionState.paymentInfoOpen) {
                setAccordionState((prev) => ({ ...prev, paymentInfoOpen: false }));
                return;
            }
            const isValid = await trigger(["AccountNumber", "InvoiceNo"]);
            if (!isValid) {
                PubSub.publish(TopicsEnum.Notification.toString(), {
                    message: "Please fill all required basic details first",
                    type: NotificationType.Failure,
                });
                return;
            }
            setAccordionState((prev) => ({
                ...prev,
                basicDetailsComplete: true,
                paymentInfoOpen: true,
                basicDetailsOpen: false,
            }));
        } else {
            setAccordionState((prev) => ({
                ...prev,
                basicDetailsOpen: section === "basicDetailsOpen" ? !prev.basicDetailsOpen : false,
            }));
        }
    }, [accordionState.paymentInfoOpen, trigger]);

    const handleNextClick = useCallback(async () => {
        const isValid = await trigger(["AccountNumber", "InvoiceNo"]);
        if (isValid) {
            setAccordionState({
                basicDetailsOpen: false,
                paymentInfoOpen: true,
                basicDetailsComplete: true,
            });
        }
    }, [trigger]);

    // ✅ ADDED: Add Row Logic
    const addPaymentRow = useCallback(() => {
        append({
            PaymentType: "",
            Amount: "",
            DueDate: "",
            GracePeriod: "",
            Description: ""
        });
        setActivePaymentIndex(fields.length); // Open the new item
    }, [append, fields.length]);

    // ✅ ADDED: Remove Row Logic
    const handleRemoveItem = (index: number) => {
        remove(index);
        if (activePaymentIndex === index) {
            setActivePaymentIndex(null);
        } else if (activePaymentIndex !== null && activePaymentIndex > index) {
            setActivePaymentIndex(activePaymentIndex - 1);
        }
    };

    const paymentTypeOptions = useMemo(() => paymentTypes.map(t => ({ Id: String(t.id), Name: t.name })), [paymentTypes]);

    const getPaymentTypeName = (id?: string) => {
        if (!id) return "Select Type";
        return paymentTypeOptions.find((p) => p.Id.toString() === id.toString())?.Name || "Select Type";
    };

    // --- Submit Logic ---
    const onSubmit = async (formData: FormValues) => {
        if (formData.payments.length === 0) {
            PubSub.publish(TopicsEnum.Notification.toString(), {
                message: "Please add at least one payment",
                type: NotificationType.Failure,
            });
            return;
        }

        setIsLoading(true);
        try {

                const currentIds = new Set(
            formData.payments
                .map(p => p.id)
                .filter((id): id is number => !!id && id > 0)
        );

        const idsToDelete = originalPaymentIds.filter(id => !currentIds.has(id));

        const deletePromises = idsToDelete.map(id =>
            apiHelper(
                PaymentService.deleteApiPaymentDueAccount,
                { showNotification: false },
                id
            )
        );



            // Determine Entity
            let entityId = organizationId;
            let entityTypeId = EntityType.Organization;

            if (formData.AttachPaymentId === "Property" && formData.PropertyId) {
                entityId = Number(formData.PropertyId);
                entityTypeId = EntityType.Property;
            } else if (formData.AttachPaymentId === "Unit" && formData.UnitId) {
                entityId = Number(formData.UnitId);
                entityTypeId = EntityType.Unit;
            }

            const paymentPromises = formData.payments.map(async (p) => {
                const isExisting = p.id && p.id > 0;
                const payload = {
                    id: p.id,
                    amount: Number(p.Amount.toString().replace(/,/g, "")),
                    description: p.Description,
                    dueDate: utcDateFormat(p.DueDate),
                    entityId: entityId,
                    entityTypeId: entityTypeId,
                    accountNumber: formData.AccountNumber,
                    payeeAccountId: Number(formData.PayeeId),
                    gracePeriodInDays: Number(p.GracePeriod) || 0,
                    referenceNumber: formData.InvoiceNo,
                    paymentTypeId: Number(p.PaymentType),
                    paidFlowTypeId: PaidFlowTypesEnum.Payable,
                    organizationId: organizationId
                };

                const apiMethod = isExisting
                    ? PaymentService.putApiPaymentDueAccount
                    : PaymentService.postApiPaymentDueAccount;

                const res = await apiHelper(
                    apiMethod,
                    { showNotification: false },
                    payload
                );

                // Document handling per payment
                if (res?.data?.success && formData.documentfile) {
                    await apiHelper(
                        DocumentService.postApiDocument,
                        { showNotification: false },
                        {
                            EntityTypeId: DocumentEntityTypesEnum.Payment,
                            EntityId: res.data.success.id,
                            DocumentTypeId: DocumentTypesEnum.PaymentReference,
                            IsMain: true,
                            FileName: formData.documentfile.name,
                            Document: new Blob([formData.documentfile]),
                        }
                    );
                }

                return res;
            });

            await Promise.all([...deletePromises, ...paymentPromises]);

            if (originalDocumentId && !formData.documentfile) {
                try {
                    await apiHelper(
                        DocumentService.deleteApiDocument,
                        { showNotification: false },
                        originalDocumentId
                    );
                    setOriginalDocumentId(null);
                } catch (deleteErr) {
                    console.warn("Failed to delete old document", deleteErr);
                    // Optionally: show a soft warning, but usually don't block success
                }
            }

            PubSub.publish(TopicsEnum.Notification.toString(), {
                message: "Payments Updated Successfully",
                type: NotificationType.Success,
                title: "Payment Updated",
                icon: <img src={GetImages.toastPaymentAccountIcon} alt="success" width="20" height="20" />,
            });

            setResetCounter((prev) => prev + 1);
            onSuccess();
            onClose();

        } catch (error) {
            PubSub.publish(TopicsEnum.Notification.toString(), {
                message: "Failed to update payment",
                type: NotificationType.Failure,
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <RightSideModal
            isOpen={isOpen}
            onClose={onClose}
            title="Edit Payment"
            buttonlabel="Update Payment"
            formId="EditPayableModalForm"
            maxWidth={600}
            className="transparent-bg"
            isSubmitDisabled={isLoading}
        >
            {isFetchingData ? (
                <div className="d-flex justify-content-center align-items-center h-100">
                    <SimpleSpinner myClassName="Icon40" />
                </div>
            ) : (
                <FormProvider {...methods}>
                    <form id="EditPayableModalForm" className="paymentModalForm" onSubmit={handleSubmit(onSubmit)}>
                        <Accordion className="style-2">
                            {/* --- Section 1: Basic Details --- */}
                            <AccordionSection
                                title="Basic Details"
                                isOpen={accordionState.basicDetailsOpen}
                                onToggle={() => toggleAccordion("basicDetailsOpen")}
                            >
                                <div className="accordion-body">
                                    <div className="row">
                                        <div className="col-6 mb-3">
                                            <label className="static-field-label">Payment Type</label>
                                            <div className="static-field-value">Payables</div>
                                        </div>
                                        <div className="col-6 mb-3">
                                            <label className="static-field-label">Payee</label>
                                            <div className="static-field-value">{data?.payerPayeeName}</div>
                                        </div>

                                        <div className="col-md-6">
                                            <InputField
                                                name="AccountNumber"
                                                label="Account Number"
                                                required
                                                requiredMessage="Account Number is required"
                                                type="text"
                                                placeholder="Enter Account Number"
                                                validation={{
                                                    required: "Account number is required",
                                                    minLength: {
                                                        value: 1,
                                                        message: "Account number is required",
                                                    },
                                                    maxLength: {
                                                        value: 50,
                                                        message: "Account number cannot exceed 50 characters",
                                                    },
                                                }}
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <InputField
                                                name="InvoiceNo"
                                                label="Invoice No./Bill No."
                                                type="text"
                                                placeholder="Enter Invoice No."
                                                required
                                                requiredMessage="Invoice No./Bill No. is required"
                                                validation={{
                                                    required: "Invoice No./Bill No. is required",
                                                    minLength: {
                                                        value: 1,
                                                        message: "Invoice No./Bill No. is required",
                                                    },
                                                    maxLength: {
                                                        value: 100,
                                                        message: "Invoice No./Bill No. cannot exceed 100 characters",
                                                    },
                                                }}
                                            />
                                        </div>

                                        <div className="col-12">
                                            <div className="form-group">
                                                <label className="fw-600 black">Upload Bill / Invoice <span className="optionalLabel">(optional)</span> </label>
                                                <DocumentUploader name="documentfile" maxSizeMB={5} resetTrigger={resetCounter} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-3">
                                        <SimpleButton type="button" label="Next" className="next-btn" onClick={handleNextClick} />
                                    </div>
                                </div>
                            </AccordionSection>

                            {/* --- Section 2: Payment Information --- */}
                            <AccordionSection
                                title="Payment Information"
                                isDisabled={!accordionState.basicDetailsComplete}
                                isOpen={accordionState.paymentInfoOpen}
                                onToggle={() => toggleAccordion("paymentInfoOpen")}
                            >
                                <div className="accordion-body">
                                    {fields.map((field, index) => {
                                        const isOpen = activePaymentIndex === index;
                                        const currentValues = watchedPayments?.[index];
                                        const paymentErrors = errors.payments?.[index];
                                        const hasError = paymentErrors && Object.keys(paymentErrors).length > 0;

                                        return (
                                            <div key={field.id} className="payment-item-card">
                                                {/* --- BODY SECTION (Inputs) --- */}
                                                <div className={`payment-item-body ${!isOpen ? "d-none" : ""}`}>
                                                    <div className="payment-item-header">
                                                        <span className="payment-item-title">Payment Details</span>
                                                        <div className="d-flex align-items-center gap-2">
                                                            {/* ✅ ADDED: Remove Button for items > 1 */}
                                                            {fields.length > 1 && (
                                                                <div className="action-icon-wrapper" onClick={() => handleRemoveItem(index)}>
                                                                    <DeleteRedIcon fill="#E02424" width="16" height="16" />
                                                                </div>
                                                            )}
                                                            <div
                                                                role="button"
                                                                onClick={() => setActivePaymentIndex(null)}
                                                                className="chevron-wrapper open"
                                                            >
                                                                <ChevronDownIcon />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="row">
                                                        <div className="col-md-6">
                                                            <SelectField
                                                                placeholderText="Select Type"
                                                                name={`payments.${index}.PaymentType`}
                                                                label="Type"
                                                                required
                                                                requiredMessage="Payment Type is required"
                                                                options={paymentTypeOptions}
                                                            />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <CurrencyInput
                                                                name={`payments.${index}.Amount`}
                                                                label={`Amount (${currencySymbol})`}
                                                                required
                                                                requiredMessage="Amount is required"
                                                                placeholder="Amount"
                                                            />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <InputField
                                                                name={`payments.${index}.DueDate`}
                                                                label="Due Date"
                                                                type="date"
                                                                required
                                                                requiredMessage="Due date is required"
                                                            />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <InputField
                                                                name={`payments.${index}.GracePeriod`}
                                                                label="Grace Period"
                                                                type="number"
                                                                required
                                                                requiredMessage="Grace period is required"
                                                                placeholder="Days"
                                                                validation={{
                                                                min: { value: 0, message: "Grace period cannot be negative" },
                                                                maxLength: {
                                                                    value: 2,
                                                                    message: "Grace period cannot exceed 99 days"
                                                                }
                                                            }}
                                                            />
                                                        </div>
                                                        <div className="col-12">
                                                            <InputField
                                                                name={`payments.${index}.Description`}
                                                                label="Description"
                                                                type="text"
                                                                placeholder="Description"
                                                                required
                                                                requiredMessage="Description is required"
                                                            validation={{
                                                                maxLength: {
                                                                    value: 250,
                                                                    message: "Description cannot exceed 250 characters"
                                                                }
                                                            }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* --- SUMMARY SECTION (Collapsed View) --- */}
                                                <div
                                                    onClick={() => setActivePaymentIndex(index)}
                                                    className={`payment-item-summary ${isOpen ? "d-none" : ""}`}
                                                    style={hasError ? { border: "1px solid #E02424", background: "#FFF5F5" } : {}}
                                                >
                                                    <div className="summary-content">
                                                        <div className="d-flex w-100 justify-content-between">
                                                            <div className="summary-col">
                                                                <div className="summary-label">Type</div>
                                                                <div className="summary-value">
                                                                    {getPaymentTypeName(currentValues?.PaymentType)}
                                                                </div>
                                                            </div>
                                                            <div className="summary-col">
                                                                <div className="summary-label">Due Date</div>
                                                                <div className="summary-value">
                                                                    {currentValues?.DueDate ? new Date(currentValues.DueDate).toLocaleDateString() : "-"}
                                                                </div>
                                                            </div>
                                                            <div className="summary-col">
                                                                <div className="summary-label">Amount</div>
                                                                <div className="summary-value">
                                                                    <CurrencyTooltip
                                                                        content={UtilHelper.CurrencyFormat(UtilHelper.parseCurrencyToNumber(currentValues?.Amount), false)}
                                                                        formatted={UtilHelper.formatCurrency(UtilHelper.parseCurrencyToNumber(currentValues?.Amount), false)}
                                                                    >
                                                                        <span>
                                                                            {UtilHelper.formatCurrency(UtilHelper.parseCurrencyToNumber(currentValues?.Amount), false)}
                                                                        </span>
                                                                    </CurrencyTooltip>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="d-flex align-items-center gap-3">
                                                        {fields.length > 1 && (
                                                            <div
                                                                role="button"
                                                                onClick={(e) => { e.stopPropagation(); handleRemoveItem(index); }}
                                                            >
                                                                <DeleteRedIcon fill="#E02424" width="16" height="16" />
                                                            </div>
                                                        )}
                                                        <div className="chevron-wrapper">
                                                            <ChevronDownIcon />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {/* ✅ ADDED: Add More Button */}
                                    <div className="d-flex align-items-center gap-2 mt-3">
                                        <SimpleOutlineButton
                                            type="button"
                                            label="+ Add More"
                                            onClick={addPaymentRow}
                                            isDisabled={fields.length >= 5}
                                        />
                                        {fields.length >= 5 && (
                                            <p className="text-danger f-12 mb-0">
                                                Maximum 5 payments allowed
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </AccordionSection>
                        </Accordion>
                    </form>
                </FormProvider>
            )}
        </RightSideModal>
    );
};

export default EditPayableModal;